/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// USE SUPABASE HOOK - Supabase client management
// ============================================================================

import { useContext, createContext } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/supabase/types";

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface SupabaseContextType {
  supabase: SupabaseClient<Database>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SupabaseContext = createContext<SupabaseContextType | undefined>(
  undefined,
);

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access the Supabase client from context
 * Follows senior TypeScript patterns with proper error handling
 */
export function useSupabase(): SupabaseContextType {
  const context = useContext(SupabaseContext);

  if (!context) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }

  return context;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface SupabaseProviderProps {
  children: React.ReactNode;
  supabase: SupabaseClient<Database>;
}

export function SupabaseProvider({
  children,
  supabase,
}: SupabaseProviderProps) {
  return (
    <SupabaseContext.Provider value={{ supabase }}>
      {children}
    </SupabaseContext.Provider>
  );
}
